import type { DeskDetail, Playbook } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { BookMarked, ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** Who wrote a version: the Lead, a person, or Deskworks (with where it came from). */
function author(p: Playbook, lead: string) {
  if (p.createdByType === 'user') return p.createdByUserName ?? t('a person');
  if (p.createdByType === 'system') return p.source || 'Deskworks';
  return p.createdByAgentName ?? lead;
}

/**
 * The desk's playbooks (docs/features/self-organizing-desks.md): how its team works on each kind of work, one playbook
 * per kind. The Lead writes and improves them after its Work Items; people read them, write one or change it.
 */
export function DeskPlaybooksSection({ desk }: { desk: DeskDetail }) {
  const lead = desk.leadAgent?.name ?? t('The Lead');
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ name: string; content: string; isNew: boolean } | null>(null);
  const invalidate = [qk.desk(desk.id)];
  const save = useApiMutation(
    (e: { name: string; content: string }) => api.desks.savePlaybook(desk.id, { name: e.name, content: e.content, changeNote: 'Edited by a person' }),
    {
      invalidate: (p) => [...invalidate, ['desk', desk.id, 'playbook', p.name]],
      success: (p) => t('Playbook “{name}” saved (version {n})', { name: p.name, n: p.version }),
      onSuccess: (p) => {
        setEditing(null);
        setOpen(p.name);
      },
    },
  );
  const remove = useApiMutation((p: Playbook) => api.desks.deletePlaybook(desk.id, p.name), { invalidate, success: t('Playbook removed') });

  return (
    <Section
      icon={<BookMarked />}
      title={t('Playbooks')}
      description={t('How the team works on each kind of work: who does what, in which order, what to check. {lead} reads the fitting one before planning and improves it after its Work Items; you can write or change one too.', { lead })}
      actions={
        !editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing({ name: '', content: '', isNew: true })}>
            <Plus /> {t('New playbook')}
          </Button>
        )
      }
    >
      {editing?.isNew && <PlaybookEditor value={editing} onChange={setEditing} onCancel={() => setEditing(null)} onSave={() => save.mutate(editing)} saving={save.isPending} />}
      {desk.playbooks.length ? (
        <ul className="flex flex-col gap-1.5 text-sm">
          {desk.playbooks.map((p) => (
            <li key={p.id} className="rounded-md border">
              <button className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left" onClick={() => setOpen(open === p.name ? null : p.name)}>
                <span className="flex min-w-0 items-center gap-2">
                  {open === p.name ? <ChevronDown className="size-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="size-4 shrink-0 text-muted-foreground" />}
                  <span className="truncate font-medium">{p.name}</span>
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {p.usedBy?.length ? `${p.usedBy.join(', ')} · ` : ''}
                  {tr('version {n} · {time}', { n: p.version, time: <RelativeTime value={p.createdAt} /> })}
                </span>
              </button>
              {open === p.name &&
                (editing && !editing.isNew && editing.name === p.name ? (
                  <div className="border-t px-3 py-3">
                    <PlaybookEditor value={editing} onChange={setEditing} onCancel={() => setEditing(null)} onSave={() => save.mutate(editing)} saving={save.isPending} />
                  </div>
                ) : (
                  <PlaybookDetail
                    desk={desk}
                    playbook={p}
                    lead={lead}
                    onEdit={() => setEditing({ name: p.name, content: p.content, isNew: false })}
                    onRemove={() => remove.mutate(p)}
                  />
                ))}
            </li>
          ))}
        </ul>
      ) : (
        !editing && <EmptyState compact title={t('No playbooks yet')} description={t('{lead} writes one when a kind of work comes up again — or write the first one yourself.', { lead })} />
      )}
    </Section>
  );
}

function PlaybookEditor({
  value,
  onChange,
  onCancel,
  onSave,
  saving,
}: {
  value: { name: string; content: string; isNew: boolean };
  onChange: (v: { name: string; content: string; isNew: boolean }) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <div className="mb-3 grid gap-2">
      {value.isNew && <Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} placeholder={t('Kind of work, e.g. SEO article')} autoFocus />}
      <Textarea
        value={value.content}
        onChange={(e) => onChange({ ...value, content: e.target.value })}
        rows={14}
        className="font-mono text-[13px] leading-relaxed"
        autoFocus={!value.isNew}
        placeholder={t('e.g.\n1. Keyword research: search volume and intent (DataForSEO)\n2. Writer drafts the outline, then the article\n3. Reviewer checks it against the checklist\n4. Ask for approval, publish, submit the URL in Search Console')}
      />
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{t('Saving creates a new version. The Lead uses it from its next run and goes on improving it.')}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t('Cancel')}
          </Button>
          <Button size="sm" disabled={!value.name.trim() || !value.content.trim()} loading={saving} onClick={onSave}>
            {t('Save')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PlaybookDetail({ desk, playbook, lead, onEdit, onRemove }: { desk: DeskDetail; playbook: Playbook; lead: string; onEdit: () => void; onRemove: () => void }) {
  const versions = useQuery({ queryKey: ['desk', desk.id, 'playbook', playbook.name], queryFn: () => api.desks.playbookVersions(desk.id, playbook.name) });
  return (
    <div className="border-t px-3 py-2">
      <div className="mb-2 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>
          {t('Version {n} by {author}', { n: playbook.version, author: author(playbook, lead) })}
          {playbook.changeNote ? ` — ${playbook.changeNote}` : ''}
        </span>
        <span className="flex gap-1">
          <Button variant="ghost" size="xs" onClick={onEdit}>
            <Pencil /> {t('Edit')}
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={t('Remove {name}', { name: playbook.name })} title={t('Remove (its versions are kept)')} onClick={onRemove}>
            <Trash2 />
          </Button>
        </span>
      </div>
      <Markdown text={playbook.content} />
      {(versions.data?.length ?? 0) > 1 && (
        <ul className="mt-3 flex flex-col gap-0.5 border-t pt-2 text-xs text-muted-foreground">
          {versions.data!.map((v) => (
            <li key={v.id}>
              {tr('Version {n} · {author} · {time}', { n: v.version, author: author(v, lead), time: <RelativeTime value={v.createdAt} /> })}
              {v.changeNote ? ` — ${v.changeNote}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
